import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthUser as User } from '@chess-platform/shared';
import * as authService from '../api/auth';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, pass: string) => Promise<void>;
    register: (email: string, pass: string, name?: string) => Promise<void>;
    logout: () => void;
    continueAsGuest: () => void;
    token: string | null;
    isGuest: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [isGuest, setIsGuest] = useState<boolean>(localStorage.getItem('isGuest') === 'true');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            try {
                const userData = await authService.getMe();
                setUser(userData);
            } catch (error) {
                setUser(null);
            }
            setLoading(false);
        };
        initAuth();
    }, []);

    const login = async (email: string, pass: string) => {
        const data = await authService.login(email, pass);
        // The token is also set in an HttpOnly cookie by the server
        setUser(data.user);
        setToken(data.access_token);
        localStorage.setItem('token', data.access_token);
    };

    const register = async (email: string, pass: string, name?: string) => {
        await authService.register(email, pass, name);
        // Auto-login after registration or just redirect to login
        await login(email, pass);
    };

    const continueAsGuest = () => {
        setIsGuest(true);
        setUser(null);
        localStorage.setItem('isGuest', 'true');
    };

    const logout = async () => {
        try {
            await authService.logout && await authService.logout();
        } catch (e) { }
        setUser(null);
        setToken(null);
        setIsGuest(false);
        localStorage.removeItem('token');
        localStorage.removeItem('isGuest');
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, continueAsGuest, token, isGuest }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
